<template>
  <div
    v-if="!layoutStore.isMobile"
    class="side-bar"
  >
    <nav class="side-box">
      <ul class="side-wrapper">
        <li
          v-for="({ title, line, path }, index) in menuList"
          :key="path"
          class="side-item"
        >
          <div
            v-if="line"
            class="side-item__line"
          />
          <NuxtLinkLocale
            v-else
            :class="{ 'side-item--focus': index === selectedMenuIndex }"
            :to="path"
            class="side-item__title"
          >
            {{ title }}
          </NuxtLinkLocale>
        </li>
      </ul>
    </nav>
  </div>

  <div
    v-else
    class="side-bar"
  >
    <ClientOnly>
      <ElSelect
        v-model="policyRouter"
        popper-class="select-pop"
        @change="handleRouterChange"
      >
        <ElOption
          v-for="item in menuList"
          :key="item.title"
          :label="item.title"
          :value="item.path"
        />
      </ElSelect>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, useLocalePath } from '#imports'
import { useLayout } from '@/store/layout'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const { t: $t } = useI18n()
const layoutStore = useLayout()

interface MenuItem {
  title: string
  path: string
  value?: string
  line?: boolean
}

const policyRouter = ref('/policy/user-agreement')
const menuList = computed<MenuItem[]>(() => [
  {
    title: $t('basic-user-agreement'),
    path: '/policy/user-agreement',
  },
  {
    title: $t('basic-privacy-policy'),
    path: '/policy/privacy-policy',
  },
  {
    title: $t('1LXimfArGeVi4fCKVtKa'),
    path: '/policy/membership-agreement',
  },
  {
    title: $t('iAjHkhm94xq4M7q72yGF'),
    path: '/policy/anti-abuse',
  },
  {
    title: $t('pweJ9RndSd1sIzpoOsOwv'),
    path: '/policy/child-sexual-abuse',
  },
  {
    title: $t('xjcgYiATpZVlotKqGkyFz'),
    path: '/policy/copyright-Infringement',
  },
  {
    title: $t('un3RslMoF53r5OBYcez'),
    path: '/policy/update',
  },
])

const route = useRoute()
const router = useRouter()
const localePath = useLocalePath()
const selectedMenuIndex = ref(0)
watch(
  route,
  newRoute => {
    selectedMenuIndex.value = menuList.value.findIndex(({ path }) => newRoute.fullPath.includes(path))
    policyRouter.value = newRoute.fullPath?.match(/\/policy\/[A-Za-z-]+/)?.[0] || '/policy/user-agreement'
  },
  {
    immediate: true,
  },
)
const handleRouterChange = (value: string) => router.replace(localePath(value))
</script>

<style lang="less" scoped>
@import '@/assets/styles/screen.less';

.side-bar {
  .pc({
    display: flex;
    overflow: hidden;
    flex: 1 0 auto;
    flex-direction: column;
    height: calc(100vh - 154px);
    min-height: 470px;
    background-color: rgb(245, 246, 250);
  });

  .mobile({
    width: 100%;
    min-height: 32px;
    margin-bottom: 16px;
  });

  .side-box {
    flex: 1;

    box-sizing: border-box;
    padding: 44px 0;

    border-radius: 8px;
  }

  .side-wrapper {
    display: flex;
    align-items: flex-start;
    flex-direction: column;

    .side-item {
      width: 100%;
    }
  }

  .side-item__line {
    height: 1px;
    margin: 16px 0;

    background: rgba(0, 0, 0, 0.1);
  }

  .side-item__title {
    display: flex;
    align-items: center;

    min-height: 44px;
    padding: 0 12px;
    margin: 0 2px; // 适配 TV 版的焦点选择

    cursor: pointer;
    white-space: nowrap;
    text-decoration: none;

    color: var(--color-secondary-text);
    border-radius: 4px;

    line-height: 1.6;
  }

  .side-item__title:hover {
    background: #ebebf0;
  }

  .side-item--focus {
    color: #306eff;
    background: rgba(48, 110, 255, 0.1);

    font-size: 14px;
    font-weight: 600;

    &:hover {
      background: rgba(48, 110, 255, 0.1);
    }
  }
}

.select-pop {
  .el-select-dropdown__item {
    color: var(--color-primary-text);

    &.selected {
      color: var(--color-primary);
    }
  }
}
</style>
